// =============================================================================
// Sell-outs + End of Day dual-run glue (Phase 3, module 1) — sibling of
// stockSync.js and same contract: every recorder is BEST-EFFORT, swallows its
// own errors, and can NEVER throw into the live till path. The Apps Script
// writes stay exactly as they are — still the source of truth.
//
// Screens call ONE function per action, passing the PIN the staff member just
// entered; the recorder verifies it per-action (ensurePass — that's what makes
// the database stamp THIS person on the row) and shadows the write into the
// 0008 tables. A Supabase hiccup means the shadow silently skips; the sheet
// write is untouched and the gap shows up in the reconcile, never on the till.
//
// Shared plumbing (host gate, site-id cache, idem, best-effort wrapper, PIN
// pass) is imported from stockSync so there is exactly ONE ?dualrun switch and
// ONE gate across both modules.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled, siteIdForKey, shadow, idem, ensurePass, currentPass } from "./stockSync.js";
import { record as recordActivity } from "./activity.js";
import * as outbox from "./selloutOutbox.js";

// Fresh at CALL time, never from React state — the day-rollover lesson
// (2026-05-15): a kiosk that slept through midnight must stamp the new day.
const londonToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
const londonNowHM = () => new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

// --- activity log line ---------------------------------------------------------
// A sell-out / restock / undo / EOD action is also a "who did what, where, when"
// event for the estate activity feed (activity.js → activity_record, 0042/0076).
// Fire-and-forget, no PIN (the database resolves the staff id from the login pass
// ensurePass just minted on the shared connection), never breaks the till. It
// writes on `supabase` — the same connection the sell-out row was written and
// signed on — and fires ONLY after that shadow write succeeded (see shadowWrite),
// so the audit line carries the same person and the same success path.
//
// The mapping is a pure, exported function so the wiring is unit-tested without a
// network round-trip; the write goes through the `_activitySink` seam.
const SELLOUT_ACTIVITY_VERB = {
  soldOut:   "sellOutMarked",
  restock:   "sellOutRestocked",
  undo:      "sellOutUndone",
  eodSubmit: "eodSubmitted",
  eodUndo:   "eodUndone",
};

export function selloutActivityLine(action, target) {
  const verb = SELLOUT_ACTIVITY_VERB[action];
  if (!verb) return null;
  return { action: verb, kind: "action", target: target || null };
}

// Test seam: production points this at the real record(); a unit test swaps it.
export const _activitySink = { record: recordActivity };

export function logSelloutActivity(siteKey, action, target) {
  const line = selloutActivityLine(action, target);
  if (!line) return null;
  try {
    return _activitySink.record({ siteKey, ...line, client: supabase });
  } catch (e) {
    console.warn("[sellout-sync] activity line threw (sell-out write unaffected):", e?.message || e);
    return null;
  }
}

// Common preamble: gate → per-action PIN pass → site uuid → best-effort write.
//
// Read-flip hardening (option B, 8 Jul): the shadow insert gets a small bounded
// retry. The 2026-07-07 Trafford "Chicken Avo" drop was a single swallowed
// insert between two successful ones — exactly the residual that becomes a
// customer-visible bug once reads flip to Supabase (a sold-out roll showing as
// available). Every insert carries an idem_key with a UNIQUE index, so a retry
// can only ever collapse to the SAME one row — it can never double-write. The
// final failure is still swallowed (best-effort contract intact, never throws
// into the till path); the nightly self-heal (reconcile_sellouts --heal) is the
// server-side backstop that closes anything the retries still miss.
const SHADOW_ATTEMPTS = 3;
// `activity` (optional): { action, target } — the "who did what" line to write to
// the estate activity feed once the shadow write lands. Fired on the SAME success
// path as the sell-out row so it carries the same pass; skipped when the shadow
// never succeeds.
async function shadowWrite(label, { staffPin, siteKey }, fn, activity = null) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  try {
    const pass = await ensurePass(staffPin, siteKey);
    if (!pass?.ok) { console.warn(`[sellout-sync] ${label} — no pass, shadow skipped`); return { ok: false, error: "no pass" }; }
    const siteId = await siteIdForKey(siteKey);
    if (!siteId) return { ok: false, error: "no site id for " + siteKey };
    let res;
    for (let attempt = 1; attempt <= SHADOW_ATTEMPTS; attempt++) {
      res = await shadow(label, () => fn(siteId));
      if (res.ok) {
        if (activity) logSelloutActivity(siteKey, activity.action, activity.target);
        return res;
      }
      if (attempt < SHADOW_ATTEMPTS) {
        console.warn(`[sellout-sync] ${label} — shadow attempt ${attempt} failed, retrying`);
        await new Promise((r) => setTimeout(r, 300 * attempt));
      }
    }
    return res;
  } catch (e) {
    console.warn(`[sellout-sync] ${label} — shadow threw:`, e?.message || e);
    return { ok: false, error: e };
  }
}

// --- durable capture (module N2 — the reliability fix) -------------------------
// Insert one queued sell-out row. Resolves the site uuid from the site KEY at
// insert time (so the durable entry survives a cold site-id cache), and treats a
// unique-violation (23505) on idem_key as SUCCESS — a retry or double-tap
// collapsing onto the row that already landed. Best-effort; never throws.
//
// ATTRIBUTION PRECONDITION (N2 self-review fix, 6 Aug): a row with no staff_id is
// REFUSED and kept queued, never landed. The 0008 BEFORE-INSERT trigger fills a
// missing staff_id with coalesce(new.staff_id, <current JWT's staff_id>) and the
// RLS insert policy checks only app_session (not identity) — so an un-stamped row
// drained under whatever session is warm on the shared client (a failed per-action
// login, or a leftover drained by the self-arming flusher / the next person's tap)
// would be permanently mis-attributed to a BYSTANDER (sell_out_events is append-only
// and reconciled on time/sku, not staff — never correctable). Keeping it queued for
// a genuine warm pass matches the old "no pass -> skip" contract; the Sheet + nightly
// self-heal backfill record such a gap honestly (staff_id null).
async function insertSellOutRow(entry) {
  if (!entry.staff_id) return { ok: false, error: "unattributed — kept for a warm pass" };
  const siteId = await siteIdForKey(entry.siteKey);
  if (!siteId) return { ok: false, error: "no site id for " + entry.siteKey };
  const row = {
    site_id: siteId, sku: entry.sku, event: entry.event,
    event_time: entry.event_time, trading_date: entry.trading_date, idem_key: entry.idem_key,
    staff_id: entry.staff_id,
  };
  if (entry.original_sold_out_time != null) row.original_sold_out_time = entry.original_sold_out_time;
  try {
    const { error } = await supabase.from("sell_out_events").insert(row);
    if (!error) return { ok: true };
    if (error.code === "23505") return { ok: true, duplicate: true }; // idem collapse — already landed
    console.warn(`[sellout-sync] insert ${entry.idem_key} rejected:`, error.message || error);
    return { ok: false, error };
  } catch (e) {
    console.warn(`[sellout-sync] insert ${entry.idem_key} threw:`, e?.message || e);
    return { ok: false, error: e };
  }
}

// Drain the durable outbox, landing every queued sell-out row and firing each
// one's activity line on the SAME success path (immediate OR flushed-later).
export function flushSellOutOutbox() {
  return outbox.flush({
    insert: insertSellOutRow,
    onLanded: (entry) => { if (entry.activity) logSelloutActivity(entry.siteKey, entry.activity.action, entry.activity.target); },
  });
}

// The recorder every sell-out screen calls. The capture-rate fix lives here:
//   1. ENQUEUE the row synchronously — durable BEFORE any await, so an immediate
//      navigate / unmount / tab-sleep can no longer lose it (the old fire-and-
//      forget failure).
//   2. Verify the PIN for correct per-action attribution, then stamp the resolved
//      staff_id onto the queued row. A slow / failed login no longer SKIPS the
//      write — it is already safe in the outbox and lands on the next warm pass.
//   3. Drain (idem-safe: a retry / double-tap can only ever collapse to one row).
// Best-effort throughout: it NEVER throws into the till path, and the Apps Script
// Sheet write stays the source of truth, untouched.
async function durableRecord(label, { staffPin, siteKey }, entryCore, activity = null) {
  if (!dualRunEnabled()) return { ok: true, skipped: true };
  try {
    const entry = { siteKey, source: "app", activity, ...entryCore };
    outbox.enqueue(entry);                              // (1) durable first
    const pass = await ensurePass(staffPin, siteKey);  // (2) per-action attribution
    if (pass?.ok) {
      try { const cur = await currentPass(); if (cur?.staffId) outbox.stampStaff(entry.idem_key, cur.staffId); }
      catch (e) { /* attribution stamp is best-effort */ }
    } else {
      console.warn(`[sellout-sync] ${label} — no pass yet; the write is queued durably and will flush on the next warm pass`);
    }
    const res = await flushSellOutOutbox();             // (3) land it (+ any leftovers)
    return { ok: res.landed > 0, queued: outbox.size() > 0 };
  } catch (e) {
    console.warn(`[sellout-sync] ${label} — durable record threw (write stays queued):`, e?.message || e);
    return { ok: false, error: e, queued: true };
  }
}

// Self-arming flusher: drain the outbox when connectivity or visibility returns,
// and once on load. Registered exactly once, only in a real dual-run browser. A
// drain with no warm pass fails-and-keeps (best-effort); the next tap's pass lands
// the leftovers.
let _flusherArmed = false;
function armFlusher() {
  if (_flusherArmed || typeof window === "undefined") return;
  _flusherArmed = true;
  const drain = () => { try { flushSellOutOutbox(); } catch (e) {} };
  try {
    window.addEventListener("online", drain);
    window.addEventListener("pageshow", drain);
    if (typeof document !== "undefined")
      document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") drain(); });
  } catch (e) { /* non-DOM env */ }
  drain(); // land anything a previous session left behind
}
if (typeof window !== "undefined" && dualRunEnabled()) armFlusher();

// --- sell-out events -----------------------------------------------------------
// Idem keys include the staff-picked TIME: unlike stock, the same roll can
// legitimately sell out twice in one day (sold out 12:00, restocked 13:30,
// sold out again 16:00). A double-tap re-sends the SAME time → one row; a real
// second cycle carries a different time → its own row. Each recorder ENQUEUES its
// row synchronously (durable) then lands it — see durableRecord above.
export function recordSoldOut({ siteKey, sku, time, staffPin, day = londonToday() }) {
  return durableRecord(`sold_out ${sku}`, { staffPin, siteKey }, {
    sku, event: "sold_out", event_time: time, trading_date: day,
    idem_key: idem(siteKey, "sellout_soldout", sku, day, time),
  }, { action: "soldOut", target: sku });
}

export function recordRestock({ siteKey, sku, time, staffPin, day = londonToday() }) {
  return durableRecord(`restock ${sku}`, { staffPin, siteKey }, {
    sku, event: "restock", event_time: time, trading_date: day,
    idem_key: idem(siteKey, "sellout_restock", sku, day, time),
  }, { action: "restock", target: sku });
}

// The undo keys on the ORIGINAL sold-out time (matching how the old backend
// pairs undos) — you can never log two undos against the same sell-out.
export function recordSellOutUndo({ siteKey, sku, originalTime, undoTime = londonNowHM(), staffPin, day = londonToday() }) {
  return durableRecord(`sellout_undo ${sku}`, { staffPin, siteKey }, {
    sku, event: "undo", event_time: undoTime, original_sold_out_time: originalTime, trading_date: day,
    idem_key: idem(siteKey, "sellout_undo", sku, day, originalTime),
  }, { action: "undo", target: sku });
}

// --- End of Day report -----------------------------------------------------------
// One ACTIVE row per site per day is enforced by the DB (0008 partial unique
// index) — a duplicate submit shadow lands as a rejected insert, logged and
// swallowed, exactly what we want while the sheet is boss.
export function recordEodSubmit({ siteKey, staffPin, wasteSummary, wasteByItem, tgtgSold, tgtgNotCollected, gateState = "unknown", stockSnapshot = null, day = londonToday(), time = londonNowHM() }) {
  // The DB refuses null TGTG by design (explicit-zero rule); mirror that here so
  // a bad call skips quietly instead of throwing into the till path.
  if (tgtgSold == null || tgtgNotCollected == null) return Promise.resolve({ ok: false, error: "tgtg not set" });
  const gs = ["free", "warn", "override"].includes(gateState) ? gateState : "unknown";
  return shadowWrite(`eod_submit ${siteKey}`, { staffPin, siteKey }, (siteId) =>
    supabase.from("daily_reports").insert({
      site_id: siteId, trading_date: day, status: "submitted",
      waste_summary: wasteSummary || "None", waste_by_item: wasteByItem || {},
      tgtg_sold: tgtgSold, tgtg_not_collected: tgtgNotCollected,
      gate_state: gs, stock_snapshot: stockSnapshot,
      idem_key: idem(siteKey, "eod_submit", day, time),
    }), { action: "eodSubmit", target: null });
}

// Real reversal, mirrored: flips today's ACTIVE row to 'undone' (the trigger
// stamps who + when). If the shadow missed the submit, this matches zero rows —
// harmless; the reconcile shows the gap on the submit, not the undo.
export function recordEodUndo({ siteKey, staffPin, day = londonToday() }) {
  return shadowWrite(`eod_undo ${siteKey}`, { staffPin, siteKey }, async (siteId) =>
    supabase.from("daily_reports")
      .update({ status: "undone", updated_at: new Date().toISOString() })
      .eq("site_id", siteId).eq("trading_date", day).eq("status", "submitted"),
    { action: "eodUndo", target: null });
}

// --- read side (the read-flip) -------------------------------------------------
// Re-exported so the FOH screen imports login/write/read all from selloutSync.
// selloutReadEnabled() is OFF by default (see sellouts.js) so importing these
// changes nothing until a site is deliberately flipped.
export { selloutReadEnabled, eodReadEnabled, fetchSellOutToday, fetchEodToday, reconstructSellOuts } from "./sellouts.js";
